import { useState } from "react";
import {
  Car,
  Fuel,
  Shield,
  DollarSign,
  MapPin,
  Star,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const UsedCarRecommendations = () => {
  const [expandedCars, setExpandedCars] = useState({});

  const toggleExpanded = (carId: string) => {
    setExpandedCars((prev) => ({
      ...prev,
      [carId]: !prev[carId as keyof typeof prev],
    }));
  };

  const cars = [
    {
      id: "prius",
      make: "Toyota",
      model: "Prius Two",
      year: 2017,
      type: "Hybrid Hatchback",
      mileage: "50-70k mi",
      price: "$12k-$14k",
      mpg: { city: 54, highway: 50, combined: 52 },
      safety: { nhtsa: 5, iihs: "Top Safety Pick" },
      features: [
        "~50+ MPG",
        "Toyota reliability",
        "Auto-Brake",
        "Lane Departure Warning",
      ],
      color: "emerald",
      description:
        "The Prius is a standout hatchback known for exceptional fuel economy. Owners report spending only ~$12 on gas every two weeks. Includes advanced Safety Sense features like automatic emergency braking, lane departure alert, and adaptive cruise control.",
      listingExample: {
        mileage: "162k mi",
        price: "$11,250",
        dealer: "Suburban Toyota (Troy)",
        note: "Look for lower-mileage options around 50-70k mi in the ~$12-14k range",
      },
    },
    {
      id: "civic",
      make: "Honda",
      model: "Civic EX",
      year: 2015,
      type: "Compact Sedan",
      mileage: "72,561 mi",
      price: "$10,995",
      mpg: { city: 30, highway: 39, combined: 33 },
      safety: { nhtsa: 4, iihs: "Good ratings" },
      features: [
        "Efficient & reliable",
        "Bluetooth & backup camera",
        "Strong resale value",
      ],
      color: "blue",
      description:
        "This Civic checks all boxes: excellent reliability, good fuel efficiency, and strong safety ratings. The EX trim brings useful features like Bluetooth, backup camera, keyless entry/start, and alloy wheels. Known to easily run past 150k miles with regular upkeep.",
      listingExample: {
        mileage: "72,561 mi",
        price: "$10,995",
        dealer: "Sky Auto Sales (Detroit)",
      },
    },
    {
      id: "elantra",
      make: "Hyundai",
      model: "Elantra Value Edition",
      year: 2020,
      type: "Sedan",
      mileage: "72,133 mi",
      price: "$13,995",
      mpg: { city: 30, highway: 40, combined: 34 },
      safety: { nhtsa: 5, iihs: "Good scores" },
      features: [
        "Sunroof",
        "Apple CarPlay",
        "Heated seats",
        "Blind-spot monitor",
        "10yr/100k warranty",
      ],
      color: "purple",
      description:
        "The Elantra offers tremendous value with strong reliability (one owner drove to 483,000 km with no major issues!). Loaded with features often not found in rivals at this price. Safety tech like automatic emergency braking and lane departure warning come standard.",
      listingExample: {
        mileage: "72,133 mi",
        price: "$13,995",
        dealer: "LaFontaine Infiniti (Ann Arbor)",
      },
    },
    {
      id: "forte",
      make: "Kia",
      model: "Forte EX",
      year: 2019,
      type: "Compact Sedan",
      mileage: "~62,900 mi",
      price: "$12,313",
      mpg: { city: 30, highway: 40, combined: 34 },
      safety: { nhtsa: 5, iihs: "Top Safety Pick" },
      features: [
        "Spacious interior",
        "Auto brake standard",
        "Long warranty",
        "Modern safety tech",
      ],
      color: "red",
      description:
        "The Forte offers excellent efficiency and modern amenities. Standard safety features include forward collision avoidance, lane-keeping assist, and driver attention monitor. The EX trim adds a large touchscreen with Apple CarPlay/Android Auto.",
      listingExample: {
        mileage: "~62k mi",
        price: "$12,313",
        dealer: "Dealer listing",
      },
    },
    {
      id: "mazda3",
      make: "Mazda",
      model: "Mazda3 Touring",
      year: 2016,
      type: "Sedan/Hatch",
      mileage: "69,229 mi",
      price: "$13,490",
      mpg: { city: 30, highway: 41, combined: 34 },
      safety: { nhtsa: 5, iihs: "Top Safety Pick+" },
      features: [
        "Fun to drive",
        "Upscale interior",
        "Smart City Brake",
        "Sporty handling",
      ],
      color: "indigo",
      description:
        "The Mazda3 combines efficiency with a sporty driving experience. Known for responsive handling and high-quality interior. Features blind-spot monitoring and cross-traffic alert on many Touring/Grand Touring models.",
      listingExample: {
        mileage: "69,229 mi",
        price: "$13,490",
        dealer: "Serra Mazda (Brighton)",
      },
    },
    {
      id: "fusion",
      make: "Ford",
      model: "Fusion Hybrid SE",
      year: 2018,
      type: "Midsize Sedan",
      mileage: "~60,000 mi",
      price: "~$13,000",
      mpg: { city: 43, highway: 41, combined: 42 },
      safety: { nhtsa: 5, iihs: "Good ratings" },
      features: [
        "Hybrid efficiency",
        "Spacious midsize",
        "Lane-Keep Assist",
        "Quiet cabin",
      ],
      color: "teal",
      description:
        "The Fusion Hybrid gives you a comfortable midsize sedan with extraordinary fuel efficiency. Known for smooth ride and quiet cabin. Safety features include Blind Spot Information System, lane-keep assist, and adaptive cruise.",
      listingExample: {
        mileage: "~62k mi",
        price: "$11,998",
        dealer: "Local dealer",
      },
    },
    {
      id: "malibu",
      make: "Chevrolet",
      model: "Malibu LT",
      year: 2016,
      type: "Midsize Sedan",
      mileage: "74,672 mi",
      price: "$14,995",
      mpg: { city: 27, highway: 37, combined: 31 },
      safety: { nhtsa: 5, iihs: "Good ratings" },
      features: [
        "Smooth ride",
        "Roomy cabin",
        "MyLink infotainment",
        "10 airbags",
      ],
      color: "amber",
      description:
        "The Malibu is a solid midsize sedan with focus on comfort and efficiency. This generation holds a 5-star NHTSA safety rating. The LT trim provides touchscreen MyLink infotainment, rear camera, and power driver's seat.",
      listingExample: {
        mileage: "74,672 mi",
        price: "$14,995",
        dealer: "Suburban Chevrolet (Ann Arbor)",
      },
    },
  ];

  const getMpgColor = (mpg: number) => {
    if (mpg >= 50) return "text-emerald-600";
    if (mpg >= 40) return "text-green-600";
    if (mpg >= 35) return "text-blue-600";
    return "text-gray-600";
  };

  const getSafetyBadgeColor = (rating: number) => {
    if (rating === 5) return "bg-green-100 text-green-800";
    if (rating === 4) return "bg-blue-100 text-blue-800";
    return "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              Used Car Recommendations
            </h1>
            <div className="flex flex-wrap justify-center gap-4 text-lg">
              <span className="flex items-center">
                <MapPin size={20} className="mr-2" />
                Ann Arbor, MI
              </span>
              <span className="flex items-center">
                <DollarSign size={20} className="mr-2" />
                Under $14,000
              </span>
              <span className="flex items-center">
                <Car size={20} className="mr-2" />
                Under 75k Miles
              </span>
              <span className="flex items-center">
                <Shield size={20} className="mr-2" />
                Top Safety Ratings
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <Fuel className="w-12 h-12 mx-auto mb-2 text-green-600" />
            <h3 className="font-semibold text-lg">Best MPG</h3>
            <p className="text-2xl font-bold text-green-600">54 MPG</p>
            <p className="text-sm text-gray-600">Toyota Prius</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <DollarSign className="w-12 h-12 mx-auto mb-2 text-blue-600" />
            <h3 className="font-semibold text-lg">Best Value</h3>
            <p className="text-2xl font-bold text-blue-600">$10,995</p>
            <p className="text-sm text-gray-600">Honda Civic EX</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-2 text-purple-600" />
            <h3 className="font-semibold text-lg">Top Safety</h3>
            <p className="text-2xl font-bold text-purple-600">6 Cars</p>
            <p className="text-sm text-gray-600">5-Star NHTSA Rating</p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-lg shadow mb-8 overflow-hidden">
          <div className="px-6 py-4 bg-gray-100 border-b">
            <h2 className="text-2xl font-bold text-gray-800">
              Quick Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mileage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MPG (City/Hwy)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Safety
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cars.map((car) => (
                  <tr
                    key={car.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleExpanded(car.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {car.year} {car.make} {car.model}
                      </div>
                      <div className="text-sm text-gray-500">{car.type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {car.price}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {car.mileage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`font-medium ${getMpgColor(
                          car.mpg.combined
                        )}`}
                      >
                        {car.mpg.city}/{car.mpg.highway}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSafetyBadgeColor(
                          car.safety.nhtsa
                        )}`}
                      >
                        {car.safety.nhtsa}★ NHTSA
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Car Cards */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Detailed Information
        </h2>
        <div className="grid gap-6">
          {cars.map((car) => (
            <div
              key={car.id}
              className={`bg-white rounded-lg shadow-lg overflow-hidden border-l-4 border-${car.color}-500`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {car.year} {car.make} {car.model}
                    </h3>
                    <p className="text-gray-600">{car.type}</p>
                  </div>
                  <button
                    onClick={() => toggleExpanded(car.id)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {expandedCars[car.id as keyof typeof expandedCars] ? (
                      <ChevronUp size={24} />
                    ) : (
                      <ChevronDown size={24} />
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Price</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {car.price}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Mileage</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {car.mileage}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">MPG Combined</p>
                    <p
                      className={`text-lg font-semibold ${getMpgColor(
                        car.mpg.combined
                      )}`}
                    >
                      {car.mpg.combined} MPG
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Safety Rating</p>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={
                            i < car.safety.nhtsa
                              ? "text-yellow-400 fill-current"
                              : "text-gray-300"
                          }
                        />
                      ))}
                      <span className="ml-2 text-sm">{car.safety.iihs}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Key Features</p>
                  <div className="flex flex-wrap gap-2">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                      >
                        <Check size={14} className="mr-1" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {expandedCars[car.id as keyof typeof expandedCars] && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Details
                      </h4>
                      <p className="text-gray-700">{car.description}</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <ExternalLink size={16} className="mr-2" />
                        Listing Example
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Mileage:</span>{" "}
                          {car.listingExample.mileage}
                        </div>
                        <div>
                          <span className="text-gray-500">Price:</span>{" "}
                          {car.listingExample.price}
                        </div>
                        <div>
                          <span className="text-gray-500">Dealer:</span>{" "}
                          {car.listingExample.dealer}
                        </div>
                      </div>
                      {car.listingExample.note && (
                        <p className="text-sm text-gray-600 mt-2 italic">
                          {car.listingExample.note}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Next Steps */}
        <div className="mt-12 bg-blue-50 rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">Next Steps</h3>
          <p className="text-gray-700 mb-4">
            All the above options are within ~30 miles of Ann Arbor. We
            recommend test-driving your top choices to see which fits you best.
            Each offers excellent fuel economy, strong safety, and low
            maintenance.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded p-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                Top Picks by Priority
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <span className="font-medium">Best MPG:</span> Toyota Prius
                  (50+ MPG)
                </li>
                <li>
                  <span className="font-medium">Best Value:</span> Honda Civic
                  EX ($10,995)
                </li>
                <li>
                  <span className="font-medium">Most Features:</span> Hyundai
                  Elantra Value Edition
                </li>
                <li>
                  <span className="font-medium">Most Fun:</span> Mazda3 Touring
                </li>
              </ul>
            </div>
            <div className="bg-white rounded p-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                Shopping Tips
              </h4>
              <ul className="space-y-2 text-sm">
                <li>• Get a pre-purchase inspection</li>
                <li>• Check CarFax/AutoCheck reports</li>
                <li>• Test drive multiple options</li>
                <li>• Negotiate based on market comparisons</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsedCarRecommendations;
